<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <!-- Main navigation wrapper -->
  <header
    class="mmm-header-desktop"
    @mouseleave="resetSubMenuSelection()"
    @focusout="resetSubMenuSelection()"
  >
    <div class="mmm-header-desktop__top-row">
      <div class="mmm-header-desktop__logo">
        <a
          :href="NavigationService.headerMenu?.rootPath"
          aria-label="Startseite"
          class="mmm-header-desktop-logo"
        >
          <Logo class="mmm-header-desktop-logo" />
        </a>
      </div>

      <!--login-->
      <div class="mmm-header-desktop__login">
        <SearchFlyout></SearchFlyout>
        <button
          class="mmm-header-desktop-login-button"
          ref="loginButtonRef"
          @click="showLoginDialog()"
          :aria-haspopup="'dialog'"
          :aria-controls="'login-dialog'"
          :aria-expanded="loginMenuOpen"
        >
          Online-Zugänge
        </button>
        <LoginMenu
          :links="loginLinks"
          ref="loginMenuDialog"
          @open="isLoginMenuOpen = true"
          @close="isLoginMenuOpen = false"
        ></LoginMenu>
      </div>
    </div>
    <nav aria-label="Unternavigation" class="mmm-header-desktop__main-nav">
      <!-- Keyboard‐navigable submenu -->
      <div
        role="menubar"
        class="mmm-header-desktop-main-nav"
        :class="{ 'mmm-header-desktop-main-nav--active': isKeyboardActive }"
        ref="menubar"
        @focusin="enableKeyboardNavigation()"
        @focusout="disableKeyboardNavigation()"
        tabindex="0"
      >
        <div
          v-for="(item, index) in navigation"
          :key="item.path"
          :class="[`mmm-header-desktop-main-nav__item--${item.accent}`]"
          class="mmm-header-desktop-main-nav__item"
        >
          <a
            :href="item.path"
            class="mmm-header-desktop-main-nav__link"
            role="menuitem"
            :aria-haspopup="item.children.length > 0 ? 'true' : undefined"
            :aria-expanded="
              item.children.length > 0
                ? subMenuSelection[0] === index
                  ? 'true'
                  : 'false'
                : undefined
            "
            :aria-controls="item.children.length > 0 ? `submenu-${index}` : undefined"
            :class="{
              'mmm-header-desktop-main-nav__link--active':
                item.active || subMenuSelection[0] === index,
              'mmm-header-desktop-main-nav__link--empty': item.children.length === 0,
            }"
            @mouseenter="setSubMenu(item, index)"
            @touchstart="handleTouchStart($event, item, index)"
            @focusin="setSubMenu(item, index)"
            tabindex="-1"
            data-nav-depth="0"
          >
            {{ sanitize(item.title) }}
          </a>
          <NavSubMenuDesktop
            @update-selection="updateSelection"
            :subMenuSelection="subMenuSelection"
            :item="item"
            :is-keyboard-active="isKeyboardActive"
            :path="[index]"
            :index="index"
            v-if="subMenuSelection[0] === index && item.children.length"
            :ref="
              el => {
                if (el && typeof el === 'object' && 'handleKeyLeft' in el) {
                  subMenuRefs[index] = el as unknown as INavSubMenuDesktopComponent;
                } else {
                  subMenuRefs[index] = null;
                }
              }
            "
          />
        </div>
      </div>
    </nav>
  </header>
  <mmm-contact></mmm-contact>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import NavSubMenuDesktop, {
  type INavSubMenuDesktopComponent,
} from '../desktop/NavSubMenuDesktop.ce.vue';
import {
  NavigationService,
  type ISubHeaderNavItem,
  EKeyCode,
  SubHeaderNavItemAdapter,
  NavTeaserService,
  sanitize,
  type THeaderConfig,
} from '../helper.ts';
import LoginMenu, { type ILoginMenuDialog } from '../shared/LoginMenu.ce.vue';
import Logo from '../shared/LogoHausbank.ce.vue';
import SearchFlyout from '../shared/SearchFlyout.ce.vue';

// Refs for DOM elements and reactive data
const menubar = ref<HTMLElement>(); // the <ul role="menubar"> element
const navigation = ref<ISubHeaderNavItem[]>([]); // the current sub‐menu tree
const loginFlyoutRef = ref<HTMLDialogElement | null>(null); // Login flyout
const loginButtonRef = ref<HTMLButtonElement | null>(null); // Login button
const loginMenuRef = ref<HTMLElement | null>(null); // Login menu
// State to indicate whether keyboard nav has been activated
const isKeyboardActive = ref(false);
const loginVisible = ref(false);
const teaserDict = ref<Record<string, THeaderConfig>>({});
const subMenuRefs = ref<(INavSubMenuDesktopComponent | null)[]>([]);

// Current navigation path in the tree, as indices per depth
const subMenuSelection = ref([-1]);

const loginMenuDialog = ref<ILoginMenuDialog>();
const isLoginMenuOpen = ref(false);
const loginLinks = NavigationService.loginMenu?.getNavigationTree() || [];

function showLoginDialog() {
  loginMenuDialog.value?.show();
}

const loginMenuOpen = computed(() => {
  return loginFlyoutRef.value?.open || false;
});

let hasTouched = false;
let touchTarget: HTMLElement | null = null;

function handleTouchStart(event: TouchEvent, item: ISubHeaderNavItem, index: number) {
  if (item.children.length > 0) {
    if (hasTouched && touchTarget !== event.target) {
      event.preventDefault();
      hasTouched = false;
      touchTarget = null;
      setSubMenu(item, index);
    } else if (!hasTouched) {
      setSubMenu(item, index);
      event.preventDefault();
      hasTouched = true;
      touchTarget = event.target as HTMLElement;
    }
  }
}

function isFocusWithinMenubar(): boolean {
  const activeElement = document.activeElement as HTMLElement;

  if (activeElement === menubar.value) return true;

  if (activeElement?.shadowRoot?.activeElement) {
    const innerActive = activeElement.shadowRoot.activeElement;
    return menubar.value?.contains(innerActive) || false;
  }

  return menubar.value?.contains(activeElement) || false;
}

async function resetSubMenuSelection(force: boolean = false) {
  if (!force) {
    await new Promise(resolve => setTimeout(resolve, 150)); // Gib Klicks Zeit
    if (isFocusWithinMenubar()) return;
  }
  subMenuSelection.value = [-1];
}

function setSubMenu(item: ISubHeaderNavItem, index: number) {
  if (isKeyboardActive.value) return;
  subMenuSelection.value = [index];
}

function disableKeyboardNavigation() {
  setTimeout(() => {
    if (!isFocusWithinMenubar()) {
      resetSubMenuSelection();
      isKeyboardActive.value = false;
      navigation.value = resetAllChildren(navigation.value);
      document.removeEventListener('keyup', handleKeyUp);
    }
  }, 150);
}

function enableKeyboardNavigation() {
  if (isKeyboardActive.value) return;
  isKeyboardActive.value = true;
  document.addEventListener('keyup', handleKeyUp, { passive: false });
}

/**
 * Global key‐up handler dispatching to arrow/tab logic.
 */
function handleKeyUp(event: KeyboardEvent) {
  if (isKeyboardActive.value === false && isKeycodeArrowKey(event.code)) {
    isKeyboardActive.value = true;
    // updateAllItems();
    return;
  }
  switch (event.code) {
    case EKeyCode.ArrowDown:
      event.stopPropagation();
      handleArrowDown();
      return;
    case EKeyCode.ArrowUp:
      event.stopPropagation();
      handleArrowUp();
      return;
    case EKeyCode.ArrowLeft:
      event.stopPropagation();
      handleArrowLeft();
      return;
    case EKeyCode.ArrowRight:
      event.stopPropagation();
      handleArrowRight();
      return;
    case EKeyCode.Escape:
      // Close all open menus
      handleEscape();
      return;
    case EKeyCode.Enter:
    case EKeyCode.Space:
      // If the current active element has a href we should redirect to it
      if (!document.activeElement) return;
      (document.activeElement as HTMLElement).click();
      return;
    case EKeyCode.Tab:
      // If focus leaves the menubar, disable keyboard nav
      if (!isFocusWithinMenubar()) {
        disableKeyboardNavigation();
      }
      return;
  }
}

function handleEscape() {
  if (subMenuSelection.value.length > 1) {
    const activeIndex = subMenuSelection.value[0];
    const submenu = subMenuRefs.value[activeIndex];
    submenu?.handleEscape?.();
  } else {
    resetSubMenuSelection(true);
    navigation.value = resetAllChildren(navigation.value);
    menubar.value?.focus();
  }
}

function handleArrowDown() {
  const activeIndex = subMenuSelection.value[0];
  const submenu = subMenuRefs.value[activeIndex];
  submenu?.handleKeyDown?.();
}

function handleArrowUp() {
  const activeIndex = subMenuSelection.value[0];
  const submenu = subMenuRefs.value[activeIndex];
  submenu?.handleKeyUp?.();
}

function handleArrowLeft() {
  if (subMenuSelection.value.length === 1) {
    selectPreviousItem();
  } else {
    const activeIndex = subMenuSelection.value[0];
    const submenu = subMenuRefs.value[activeIndex];
    submenu?.handleKeyLeft?.();
  }
}

function handleArrowRight() {
  if (subMenuSelection.value.length === 1) {
    selectNextItem();
  } else {
    const activeIndex = subMenuSelection.value[0];
    const submenu = subMenuRefs.value[activeIndex];
    submenu?.handleKeyRight?.();
  }
}

function selectPreviousItem() {
  if (!menubar.value) return;
  const targets = menubar.value?.querySelectorAll('[data-nav-depth="0"]');
  if (subMenuSelection.value[0] <= 0) {
    subMenuSelection.value[0] = targets.length - 1;
  } else {
    subMenuSelection.value[0]--;
  }
  const nextTarget = targets[subMenuSelection.value[0]] as HTMLElement;
  nextTarget.focus();
}

function selectNextItem() {
  if (!menubar.value) return;
  const targets = menubar.value?.querySelectorAll('[data-nav-depth="0"]');
  if (subMenuSelection.value[0] === targets.length - 1) {
    subMenuSelection.value = [0];
  } else {
    subMenuSelection.value[0]++;
  }
  const nextTarget = targets[subMenuSelection.value[0]] as HTMLElement;
  nextTarget.focus();
}

function updateSelection(selection: number[], ignoreFocus: boolean) {
  subMenuSelection.value = selection;
  if (ignoreFocus) return;
  if (subMenuSelection.value.length === 1 && subMenuSelection.value[0] >= 0) {
    subMenuSelection.value[0]++;
    selectPreviousItem();
  }
}

/**
 * Recursively clear focused/open flags on every node.
 */
function resetAllChildren(nodes: ISubHeaderNavItem[]): ISubHeaderNavItem[] {
  return nodes.map(node => ({
    ...node,
    focused: false,
    open: false,
    children: resetAllChildren(node.children),
  }));
}

function isKeycodeArrowKey(code: string) {
  return (
    [EKeyCode.ArrowDown, EKeyCode.ArrowLeft, EKeyCode.ArrowRight, EKeyCode.ArrowUp].indexOf(
      code as EKeyCode
    ) > -1
  );
}

function stopScrollingWithKeypress(e: KeyboardEvent) {
  if (isKeycodeArrowKey(e.code) && isKeyboardActive.value) {
    e.preventDefault();
  }
}

function hideLoginMenu() {
  if (!loginFlyoutRef.value) return;
  loginFlyoutRef.value.close();
  loginVisible.value = false;
}

function handleClickOutside(event: MouseEvent) {
  const path = event.composedPath();

  if (
    loginFlyoutRef.value &&
    !path.includes(loginMenuRef.value as Node) &&
    !path.includes(loginButtonRef.value as Node)
  ) {
    hideLoginMenu();
  }

  if (menubar.value && !path.includes(menubar.value)) {
    disableKeyboardNavigation();
  }
}

//#endregion

// On component mount, load menu data and adapt to our internal format
onMounted(async () => {
  await NavigationService.whenReady();
  await NavTeaserService.whenReady();
  if (NavigationService.headerMenu && NavigationService.headerMenu.topLevel) {
    navigation.value = NavigationService.headerMenu
      .getSubNavigation()
      .map(s => SubHeaderNavItemAdapter.create(s));
  }
  teaserDict.value = NavTeaserService.teaserConfig;
  window.addEventListener('keydown', stopScrollingWithKeypress, false);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', stopScrollingWithKeypress);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
@use '../../../common/styles/utils/utils';

.lowest-level {
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  width: 100%;
  height: calc(var(--default-min-height) + 10px);
  z-index: 10;
  padding: var(--sub-level-gap);
  box-sizing: border-box;
  list-style-type: none;
  margin: 0;
  left: calc(100% - var(--sub-level-gap));
  padding-top: 58px;

  &--visible,
  &:hover {
    display: block;
  }

  &::before,
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    display: block;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100%;
    pointer-events: none;
  }

  &::before {
    z-index: -1;
    width: calc(100% - var(--sub-level-gap));
    background-color: var(--overlay-background-third-level);
    left: calc(var(--sub-level-gap) * 0.5);
  }

  &:after {
    left: 100%;
    background-color: var(--overlay-background);
  }

  &__item {
    &--active {
      font-weight: 700;

      .lowest-level__link {
        color: var(--current-accent);
        background-color: var(--sub-menu-item-active-background);
        border-radius: 5px;
      }

      + .lowest-level__item {
        .lowest-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:hover {
      border-radius: 5px;

      .lower-level__link {
        border-top-color: transparent;
        background-color: var(--sub-menu-item-hover-background);
        border-radius: 5px;
        color: var(--current-accent);
        font-weight: bold;
      }

      + .lowest-level__item {
        .lowest-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:first-child {
      .lowest-level__link {
        border-top: none;
      }
    }
  }

  &__link {
    padding: 10px;
    color: var(--default-text-color);
    padding: 8px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;
    display: flex;
    gap: 10px;
    font-size: 14px;
    padding-left: 12px;
    padding-right: 12px;
    border-top: 1px solid var(--sub-menu-item-hover-background);

    &:hover,
    &--active {
      background-color: var(--sub-menu-item-hover-background);
      color: var(--current-accent);
      border-radius: 5px;
      font-weight: bold;
    }
  }
}

.lower-level {
  display: none;
  position: absolute;
  list-style-type: none;
  height: calc(var(--default-min-height) + 10px);
  left: 100%;
  list-style-type: none;
  margin: -60px 0 -6px 0;
  padding: 58px var(--sub-level-gap) 30px;
  top: 0;
  width: 100%;
  z-index: 10;

  &--visible,
  &:hover {
    display: block;
  }

  &::before,
  &:after {
    content: '';
    position: absolute;
    background-color: var(--overlay-background-second-level);
    width: 100%;
    display: block;
    top: 0;
    left: var(--sub-level-gap);
    bottom: 0;
    height: 100%;
    pointer-events: none;
  }

  &::before {
    z-index: -1;
    width: calc(100% - var(--sub-level-gap) / 2);
    left: calc(var(--sub-level-gap) * 0.5);
  }

  &:after {
    left: calc(100% - var(--sub-level-gap));
    width: 200%;
    background-color: var(--overlay-background-second-level);
    z-index: -2;
  }

  &__item {
    &--active {
      font-weight: 700;

      .lower-level__link {
        color: var(--current-accent);
        background-color: var(--sub-menu-item-active-background);
        border-radius: 5px;
      }

      + .lower-level__item {
        .lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:hover {
      border-radius: 5px;

      .lower-level__link {
        border-top-color: transparent;
        background-color: var(--sub-menu-item-hover-background);
        border-radius: 5px;
        color: var(--current-accent);
        font-weight: bold;
      }

      + .lower-level__item {
        .lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:first-child {
      .lower-level__link {
        border-top: none;
      }
    }
  }

  &__link {
    padding: 10px;
    color: var(--default-text-color);
    padding: 8px 0;
    display: flex;
    align-items: center;
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 14px;
    padding-left: 12px;
    padding-right: 12px;
    border-top: 1px solid var(--sub-menu-item-hover-background);

    &:hover,
    &--active {
      color: var(--current-accent);
      border-radius: 5px;
      font-weight: bold;
    }
  }
}

.mmm-header-desktop {
  width: 100%;
  position: relative;

  &__top-row {
    display: flex;
    max-width: 1492px;
    width: 100%;
    margin: auto;
    height: 60px;
    align-items: center;
    padding: 0 64px;
    box-sizing: border-box;
  }

  &__logo {
    display: flex;
    align-items: flex-end;
    flex: 1 1 50%;
  }

  &__login {
    display: flex;
    align-items: flex-end;
    gap: 24px;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__main-nav {
    display: flex;
    align-items: flex-end;
    max-width: 1492px;
    width: 100%;
    margin: auto;
    height: 60px;
    padding: 0 64px;
    box-sizing: border-box;

    &:has(.mmm-header-desktop-main-nav:hover) {
      .mmm-header-desktop__sub-nav {
        display: flex;
      }
    }
  }

  &__sub-nav {
    position: absolute;
    bottom: 0;
    transform: translate(-50%, 100%);
    left: 50%;
    width: 100vw; // change this to 100% to make it a box
    display: none;
    flex-direction: column;
    grid-column: 1 / -1;
    /* nimmt die volle Breite */

    &:hover,
    &:has(a:focus),
    &--active {
      display: flex;
    }

    &::after {
      content: '';
      height: 1px;
      bottom: 0;
      left: 0;
      width: 100%;
      display: block;
      position: absolute;
      z-index: -1;
    }
  }
}

.mmm-header-desktop-main-nav {
  display: flex;
  gap: 25px;
  margin: 0;
  padding: 0;
  list-style-type: none;
  height: 40px;
  align-items: center;

  &__item {
    &--first {
      --current-accent: var(--first-accent);
    }

    &--second {
      --current-accent: var(--second-accent);
    }

    &--third {
      --current-accent: var(--third-accent);
    }

    &--forth {
      --current-accent: var(--forth-accent);
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    height: 35px;
    border-bottom: 5px solid transparent;
    font-weight: 700;
    text-decoration: none;
    color: var(--color-foreground-text);

    &:hover,
    &--active {
      border-color: var(--current-accent);
      color: var(--current-accent);
    }
  }
}

.mmm-header-desktop-logo {
  display: flex;
  align-items: flex-end;
  transform: translate(-1px, 2px);
}

.mmm-header-desktop-login-button {
  padding: 0 28px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: var(--color-primary-login);
  color: var(--color-primary-login-contrast);
  font-weight: 700;
  border-radius: 5px;
  border: none;
}

.mmm-header-desktop-sub-nav {
  width: 100%;
  height: 100%;
  background-color: var(--overlay-background);
  height: 100%;
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &--first {
    --current-accent: var(--first-accent);
  }

  &--second {
    --current-accent: var(--second-accent);
  }

  &--third {
    --current-accent: var(--third-accent);
  }

  &--forth {
    --current-accent: var(--forth-accent);
  }

  &__wrapper {
    width: 100%;
    max-width: var(--header-desktop-max-width);
    padding: 0 65px;
    position: relative;
    margin: auto;
    display: flex;
    box-sizing: border-box;
    flex: 1 1 100%;
    display: flex;
  }

  &__teaser {
    flex: 1 1 auto;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 32px;
  }

  &__navigation {
    flex: 1 1 100%;
    overflow: hidden;
    padding-right: 12px;
  }

  &__group {
    display: grid;
    flex-wrap: wrap;
    grid-template-columns: 1fr 1fr 1fr;
    gap: var(--sub-level-gap);
    padding: 26px 0 0;
    margin: 0;
    transition: all ease-in-out 0.15s;
  }

  &__category {
    list-style-type: none;
    margin: 0;
    padding: 0;
    flex: 0 0 33%;

    font-weight: bold;

    &-title {
      padding-top: 14px;

      &-link {
        display: blocK;
        color: var(--default-text-color);
        font-size: 20px;
        text-decoration: none;
        margin-bottom: 10px;
      }
    }

    &-nav {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
  }
}

.mmm-header-desktop-sub-nav-lower-level {
  display: flex;
  flex-direction: column;
  position: relative;

  &__item {
    &--active {
      font-weight: 700;

      .mmm-header-desktop-sub-nav-lower-level__link {
        color: var(--current-accent);
        background-color: var(--sub-menu-item-active-background);
        border-radius: 5px;
      }

      + .mmm-header-desktop-sub-nav-lower-level__item {
        .mmm-header-desktop-sub-nav-lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:hover {
      border-radius: 5px;

      .mmm-header-desktop-sub-nav-lower-level__link {
        border-top-color: transparent;
        background-color: var(--sub-menu-item-active-background);
        // background-color: var(--sub-menu-item-hover-background);
        border-radius: 5px;
        color: var(--current-accent);
        font-weight: bold;
      }

      + .mmm-header-desktop-sub-nav-lower-level__item {
        .mmm-header-desktop-sub-nav-lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:first-child {
      .mmm-header-desktop-sub-nav-lower-level__link {
        border-top: none;
      }
    }
  }

  &__link {
    color: var(--default-text-color);
    text-decoration: none;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 14px;
    padding-right: 12px;
    border-top: 1px solid var(--sub-menu-item-active-background);

    &-text {
      display: inline-block;
    }
  }

  &__icon {
    height: 12px;
    width: 12px;
  }
}
</style>
